/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 检测中动画页面组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-02-05 16:03:28
 */

import React from 'react'

import './DetectionInProgress.scss'
import {
  CheckOutlined,
  CloseOutlined,
  SafetyCertificateOutlined,
  SearchOutlined
} from '@ant-design/icons'

const SEARCH_MODULES = ['特征提取引擎', '相似内容检索', '智能分组整理']

interface DetectionInProgressProps {
  variant?: 'progress' | 'notice'
  title?: string
  description?: string
  descriptionLines?: string[]
  statusLabel?: string
  showStats?: boolean
  showCancel?: boolean
  primaryActionText?: string
  onPrimaryAction?: () => void
  showFooter?: boolean
}

const DetectionInProgress: React.FC<DetectionInProgressProps> = ({
  variant = 'progress',
  title = '正在搜索相似照片',
  description = 'AI 正在检索当前时间范围内的照片特征，并尝试建立相似分组。',
  descriptionLines,
  statusLabel = '处理中',
  showStats = true,
  showCancel = true,
  primaryActionText = '知道了',
  onPrimaryAction,
  showFooter = true
}) => {
  const contentLines =
    descriptionLines && descriptionLines.length > 0
      ? descriptionLines
      : [description]

  if (variant === 'notice') {
    return (
      <div className="detection-in-progress detection-in-progress--notice">
        <div className="background-effects">
          <div className="effect-blob blob-primary"></div>
          <div className="effect-blob blob-purple"></div>
        </div>

        <div className="notice-layout">
          <div className="notice-hero">
            <div className="notice-orbit">
              <div className="notice-orbit-ring notice-orbit-ring--outer" />
              <div className="notice-orbit-ring notice-orbit-ring--inner" />
              <div className="notice-orbit-core">
                <CheckOutlined className="notice-orbit-icon" />
              </div>
            </div>
            <span className="notice-status-pill">{statusLabel}</span>
          </div>

          <div className="notice-copy">
            <h2 className="notice-title">{title}</h2>
            <p className="notice-subtitle">
              当前团队已获得 AI 相似照片检测能力，系统正在完成首次准备。
            </p>
          </div>

          <div className="notice-points">
            {contentLines.map((line, index) => (
              <div key={`${line}-${index}`} className="notice-point">
                <span className="notice-point-icon">
                  <SafetyCertificateOutlined />
                </span>
                <span className="notice-point-text">{line}</span>
              </div>
            ))}
          </div>

          <div className="notice-actions">
            <button
              type="button"
              className="notice-primary-btn"
              onClick={onPrimaryAction}
            >
              {primaryActionText}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="detection-in-progress">
      <div className="background-effects">
        <div className="effect-blob blob-primary"></div>
        <div className="effect-blob blob-purple"></div>
      </div>

      <div className="content-wrapper">
        <div className="search-stage">
          <div className="scanner-shell">
            <div className="scanner-grid" />
            <div className="scanner-ring scanner-ring--outer" />
            <div className="scanner-ring scanner-ring--mid" />
            <div className="scanner-ring scanner-ring--inner" />
            <div className="scanner-beam" />
            <div className="scanner-center">
              <SearchOutlined className="scanner-center-icon" />
            </div>
            <span className="scanner-node scanner-node--one" />
            <span className="scanner-node scanner-node--two" />
            <span className="scanner-node scanner-node--three" />
            <span className="scanner-node scanner-node--four" />
          </div>

          <div className="search-status-bar">
            <span className="search-status-dot" />
            <span className="search-status-text">{statusLabel}</span>
          </div>
        </div>

        <div className="text-content">
          <h2 className="main-title">{title}</h2>
          {contentLines.map((line, index) => (
            <p key={`${line}-${index}`} className="description">
              {line}
            </p>
          ))}
        </div>

        {showStats && (
          <div className="search-modules">
            {SEARCH_MODULES.map((moduleName, index) => (
              <div key={moduleName} className="search-module">
                <span className="search-module-index">0{index + 1}</span>
                <span className="search-module-text">{moduleName}</span>
              </div>
            ))}
          </div>
        )}

        {showCancel && (
          <div className="actions-section">
            <button className="cancel-btn">
              <CloseOutlined className="cancel-icon" />
              取消扫描
            </button>
          </div>
        )}
      </div>

      {showFooter && (
        <div className="page-footer">
          <div className="footer-status">
            <div className="status-dot"></div>
            <span>服务器已连接</span>
          </div>
          <span className="version">v2.4.0-stable</span>
        </div>
      )}
    </div>
  )
}

export default DetectionInProgress
